import { useEffect, useState } from 'react'

import { api } from '../api/client'
import type { AiPeriodo } from '../api/client'
import type { AiUsageByAsset, AiUsageStats } from '../api/types'
import { BottoniEsporta } from '../components/BottoniEsporta'
import { useAmministra } from '../components/BusinessApps'
import { Card, EmptyState, ErrorBanner, StatCard, Spinner, formatDay } from '../components/common'
import { DataTable, type Column } from '../components/DataTable'
import {
  CategoryBars, CountSeries, Donut, HeatmapSettimana, StackedCountSeries, TINTA_ALTRI, TINTE_SERIE, formatBytes,
} from '../components/charts'
import type { StackPoint, StackSerie } from '../components/charts'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

// Etichette delle categorie con t() letterale (la guardia i18n le vede).
const CATEGORIE_LABEL: Record<string, () => string> = {
  chat: () => t('Chat generativa'),
  coding: () => t('Assistente di codice'),
  image: () => t('Generazione immagini'),
  search: () => t('Ricerca / risposte'),
  other: () => t('Altro'),
}
const labelCategoria = (c: string) => CATEGORIE_LABEL[c]?.() ?? c

/**
 * Utilizzo AI: quali servizi AI esterni si usano in rete e da chi.
 *
 * Il dato viene dall'SNI che la sonda vede in ascolto passivo, confrontato con
 * il catalogo dei servizi AI. «Chi» è l'asset (e l'utente dove noto). Il
 * rapporto per asset può essere **anonimizzato**: PC e utente come pseudonimi,
 * rivelabili solo con la chiave giusta (Impostazioni → Privacy).
 */
/** Data ISO (aaaa-mm-gg) di oggi meno ``giorni``, nell'ora locale. */
function isoGiorniFa(giorni: number): string {
  const d = new Date()
  d.setDate(d.getDate() - giorni)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** Perché un periodo scelto non va, o null se va (gli stessi limiti del server). */
function erroreIntervallo(da: string, a: string): string | null {
  if (!da || !a) return t('Indica la data d’inizio e quella di fine.')
  if (da > a) return t('L’inizio viene dopo la fine.')
  const giorni = (Date.parse(a) - Date.parse(da)) / 86_400_000 + 1
  if (giorni > 366) return t('Il periodo può essere al massimo di 366 giorni.')
  return null
}

export default function AiUsage() {
  const stato = useAsync(() => api.aiUsageStatus(), [])
  const [scelta, setScelta] = useState<string>('30')
  const [dal, setDal] = useState(isoGiorniFa(29))
  const [al, setAl] = useState(isoGiorniFa(0))
  const errore = scelta === 'custom' ? erroreIntervallo(dal, al) : null
  // L'ultimo periodo valido: mentre si scrive una data a metà, la pagina resta
  // su quello di prima invece di svuotarsi.
  const [periodo, setPeriodo] = useState<AiPeriodo>({ days: 30 })
  useEffect(() => {
    if (scelta !== 'custom') setPeriodo({ days: Number(scelta) })
    else if (!errore) setPeriodo({ from: dal, to: al })
  }, [scelta, dal, al, errore])

  if (stato.loading && !stato.data) return <Spinner />
  if (stato.data && !stato.data.available) {
    return (
      <>
        <header className="page__header">
          <h1 className="page__title">{t('Utilizzo AI')}</h1>
        </header>
        <EmptyState
          title={t('Non compreso nella licenza')}
        />
      </>
    )
  }

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Utilizzo AI')}</h1>
          <p className="page__subtitle">
            {t('Quali servizi AI esterni vengono usati in rete e da quali asset, da ciò che vedono sonde, agenti e flussi. Solo metadati: nessun contenuto, nessuna decrittazione.')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label className="field" style={{ marginBottom: 0, maxWidth: 180 }}>
            <span className="field__label">{t('Periodo')}</span>
            <select value={scelta} onChange={(e) => setScelta(e.target.value)}>
              <option value="7">{t('7 giorni')}</option>
              <option value="30">{t('30 giorni')}</option>
              <option value="90">{t('90 giorni')}</option>
              <option value="365">{t('Un anno')}</option>
              <option value="custom">{t('Personalizzato…')}</option>
            </select>
          </label>
          {scelta === 'custom' && (
            <>
              <label className="field" style={{ marginBottom: 0 }}>
                <span className="field__label">{t('Dal')}</span>
                <input type="date" value={dal} max={al || undefined} onChange={(e) => setDal(e.target.value)} />
              </label>
              <label className="field" style={{ marginBottom: 0 }}>
                <span className="field__label">{t('Al')}</span>
                <input type="date" value={al} min={dal || undefined} onChange={(e) => setAl(e.target.value)} />
              </label>
            </>
          )}
          <BottoniEsporta path={(fmt) => api.aiUsageExportPath(fmt, periodo)} nome="censo-utilizzo-ai" />
        </div>
      </header>

      {errore && <p className="banner banner--warn">{errore}</p>}
      <Diagnostica />
      <Statistiche periodo={periodo} />
      <PerAsset periodo={periodo} />
      <Privacy />
    </>
  )
}

/**
 * Diagnostica «perché rileva o no»: licenza, catalogo seminato, mappa IP per il
 * NetFlow e ultimo avvistamento. Un catalogo o una mappa a zero spiegano da soli
 * un «non rileva nulla».
 */
function Diagnostica() {
  const d = useAsync(() => api.aiUsageDiagnostics(), [])
  if (!d.data) return null
  const x = d.data
  const quando = x.last_sighting_at ? new Date(x.last_sighting_at).toLocaleString() : t('mai')
  return (
    <Card title={t('Diagnostica')}>
      <div className="stats">
        <StatCard label={t('Modulo')} value={x.licensed ? t('attivo') : t('non attivo')} />
        <StatCard label={t('Servizi nel catalogo')} value={x.catalog_count} />
        <StatCard label={t('IP riconosciuti (NetFlow)')} value={`${x.ip_map_count} (${x.ip_map_sni} ${t('certi')})`} />
        <StatCard label={t('Agenti che leggono i nomi')} value={x.agents_reading ?? 0} />
        <StatCard label={t('IP AI tenuti dal filtro flussi')} value={x.flow_keep_count ?? 0} />
        <StatCard label={t('Ultimo avvistamento')} value={quando} />
        <StatCard label={t('Righe d’uso')} value={x.usage_rows} />
      </div>
      {x.catalog_count === 0 && (
        <p className="muted">{t('Il catalogo è vuoto: il modulo non può riconoscere nulla. Di solito è una migrazione non applicata — riavvia il Control Plane.')}</p>
      )}
      {(x.flow_keep_count ?? 0) > 0 && (
        <p className="muted">{t('Il filtro dei flussi tiene sempre il traffico verso questi {n} indirizzi di servizi AI, anche con il perimetro «entrambi interni»: le sonde li ricevono con la configurazione dei flussi, così il volume e il riconoscimento dai flussi non si perdono.', { n: x.flow_keep_count ?? 0 })}</p>
      )}
      {x.catalog_count > 0 && x.ip_map_count === 0 && (
        <p className="muted">{t('Nessun IP ancora in mappa: il riconoscimento dai flussi NetFlow parte quando un mirror vede l’SNI (o dopo la risoluzione DNS del catalogo). Con un mirror attivo l’SNI rileva già da solo.')}</p>
      )}
      {(x.netflow_blind ?? []).length > 0 && (
        <p className="muted">
          {t('Dai soli flussi NetFlow non si riconoscono: {s}. Stanno su indirizzi condivisi (Cloudflare, Google, Microsoft, Akamai) con migliaia di altri siti, quindi un flusso verso di loro non dice quale sito. Li riconoscono l’ascolto passivo della sonda (il nome TLS, anche in HTTP/3, e le domande DNS) e l’agente CENSO sulla macchina, che vede i nomi che risolve.', { s: (x.netflow_blind ?? []).join(', ') })}
          {(x.netflow_visible ?? []).length > 0 && ` ${t('Dai flussi sì: {s}.', { s: (x.netflow_visible ?? []).join(', ') })}`}
        </p>
      )}
    </Card>
  )
}

// I tipi hanno una tinta fissa: il colore segue il tipo, non la sua posizione.
const TINTA_CATEGORIA: Record<string, string> = {
  chat: TINTE_SERIE[0], coding: TINTE_SERIE[1], image: TINTE_SERIE[2], search: TINTE_SERIE[3], other: TINTA_ALTRI,
}

/** «+35%», «−12%», «nuovo» o «=» rispetto al periodo precedente. */
function variazione(ora: number, prima: number): { testo: string; tono: 'ok' | 'warn' | 'muted' } {
  if (!prima && !ora) return { testo: '—', tono: 'muted' }
  if (!prima) return { testo: t('nuovo'), tono: 'warn' }
  const pct = Math.round(((ora - prima) / prima) * 100)
  if (pct === 0) return { testo: '=', tono: 'muted' }
  return { testo: `${pct > 0 ? '+' : '−'}${Math.abs(pct)}%`, tono: pct > 0 ? 'warn' : 'ok' }
}

/** Il suggerimento sotto un numero: com'era nel periodo precedente. */
function rispettoAPrima(ora: number, prima: number): string {
  const v = variazione(ora, prima)
  return v.testo === '—' ? '' : t('{v} rispetto al periodo precedente ({p})', { v: v.testo, p: prima.toLocaleString(localeCorrente()) })
}

function Variazione({ ora, prima }: { ora: number; prima: number }) {
  const v = variazione(ora, prima)
  return <span className={`aistat__delta aistat__delta--${v.tono}`} title={t('rispetto al periodo precedente della stessa durata')}>{v.testo}</span>
}

/** Le ore UTC di ogni giorno portate all'ora locale (ora legale compresa):
 *  `celle[giorno][ora]` con il lunedì come giorno 0. */
function celleLocali(orarie: AiUsageStats['hourly']): number[][] {
  const celle = Array.from({ length: 7 }, () => Array(24).fill(0) as number[])
  for (const h of orarie) {
    const d = new Date(`${h.day}T${String(h.hour).padStart(2, '0')}:00:00Z`)
    if (Number.isNaN(d.getTime())) continue
    celle[(d.getDay() + 6) % 7][d.getHours()] += h.count
  }
  return celle
}

/** Fuori orario: prima delle 8, dalle 19 in poi, o nel fine settimana. */
function quotaFuoriOrario(celle: number[][]): { fuori: number; weekend: number; totale: number; oraPiena: number } {
  let fuori = 0
  let weekend = 0
  let totale = 0
  const perOra = Array(24).fill(0) as number[]
  celle.forEach((riga, d) => riga.forEach((v, h) => {
    totale += v
    perOra[h] += v
    if (d >= 5) weekend += v
    if (d >= 5 || h < 8 || h >= 19) fuori += v
  }))
  return { fuori, weekend, totale, oraPiena: perOra.indexOf(Math.max(...perOra)) }
}

function etichettaFonteStat(f: string): string {
  if (f === 'sni') return t('sonda (SNI)')
  if (f === 'agent') return t('agente')
  if (f === 'netflow') return t('flussi NetFlow')
  return f
}

/**
 * Le statistiche: andamento nel tempo per servizio o per tipo, confronto con
 * il periodo precedente, quando (giorno × ora, fuori orario), adozione, fonti
 * e novità. Aggregati: nessun nome, quindi sempre mostrabili.
 */
/** Una chiave stabile del periodo, per le dipendenze degli effetti. */
function chiavePeriodo(p: AiPeriodo): string {
  return 'days' in p ? `d${p.days}` : `${p.from}_${p.to}`
}

function Statistiche({ periodo }: { periodo: AiPeriodo }) {
  const dati = useAsync(() => api.aiUsageStats(periodo), [chiavePeriodo(periodo)])
  const [vista, setVista] = useState<'service' | 'category'>('service')
  if (dati.loading && !dati.data) return <Spinner />
  const st = dati.data
  if (!st) return <ErrorBanner error={dati.error} />
  const k = st.kpi
  const fmt = (n: number) => n.toLocaleString(localeCorrente())

  if (!k.uses) {
    return (
      <>
        <ErrorBanner error={dati.error} />
        <Card title={t('Statistiche')}>
          <EmptyState title={t('Nessun uso di AI rilevato')} hint={t('Nessun traffico verso i servizi AI del catalogo nel periodo scelto.')} />
        </Card>
      </>
    )
  }

  // Le serie: i primi quattro servizi per nome, gli altri in «altri».
  const serieServizi: StackSerie[] = [
    ...st.top_services.map((s, i) => ({ key: s, label: s, color: TINTE_SERIE[i] })),
    ...(st.daily.some((d) => (d.by_service.__altri__ ?? 0) > 0)
      ? [{ key: '__altri__', label: t('altri servizi'), color: TINTA_ALTRI }] : []),
  ]
  const categoriePresenti = st.categories.map((c) => c.category)
  const serieCategorie: StackSerie[] = ['chat', 'coding', 'image', 'search', 'other']
    .filter((c) => categoriePresenti.includes(c))
    .concat(categoriePresenti.filter((c) => !(c in TINTA_CATEGORIA)))
    .map((c) => ({ key: c, label: labelCategoria(c), color: TINTA_CATEGORIA[c] ?? TINTA_ALTRI }))
  const punti: StackPoint[] = st.daily.map((d) => ({
    date: d.day, values: vista === 'service' ? d.by_service : d.by_category,
  }))

  const celle = celleLocali(st.hourly)
  const quando = quotaFuoriOrario(celle)
  const pct = (n: number, tot: number) => (tot ? Math.round((n / tot) * 100) : 0)
  const giorniSettimana = [t('Lun'), t('Mar'), t('Mer'), t('Gio'), t('Ven'), t('Sab'), t('Dom')]
  const ultimoCumulato = st.adoption.length ? st.adoption[st.adoption.length - 1].cumulative : 0

  const colonneServizi: Column<AiUsageStats['services'][number]>[] = [
    { key: 'service', header: t('Servizio') },
    { key: 'category', header: t('Tipo'), value: (s) => labelCategoria(s.category) },
    { key: 'count', header: t('Usi'), type: 'number', render: (s) => fmt(s.count) },
    {
      key: 'variazione',
      header: t('Rispetto a prima'),
      value: (s) => variazione(s.count, s.count_prev).testo,
      render: (s) => <Variazione ora={s.count} prima={s.count_prev} />,
    },
    { key: 'assets', header: t('Asset'), type: 'number' },
    { key: 'days_active', header: t('Giorni con uso'), type: 'number', hidden: true },
    {
      key: 'bytes',
      header: t('Volume'),
      type: 'number',
      value: (s) => s.bytes || null,
      render: (s) => (s.bytes ? formatBytes(s.bytes) : '—'),
    },
    { key: 'sources', header: t('Fonte'), value: (s) => s.sources.map(etichettaFonteStat).join(', ') },
    { key: 'first_seen', header: t('Prima volta'), type: 'date', hidden: true, render: (s) => formatDay(s.first_seen) },
    { key: 'last_seen', header: t('Ultima volta'), type: 'date', render: (s) => formatDay(s.last_seen) },
  ]

  return (
    <>
      <ErrorBanner error={dati.error} />
      <div className="stats">
        <StatCard label={t('Usi')} value={fmt(k.uses)} hint={rispettoAPrima(k.uses, k.uses_prev)} />
        <StatCard label={t('Asset coinvolti')} value={fmt(k.assets)} hint={rispettoAPrima(k.assets, k.assets_prev)} />
        <StatCard label={t('Servizi AI usati')} value={fmt(k.services)} hint={rispettoAPrima(k.services, k.services_prev)} />
        <StatCard label={t('Asset alla prima volta')} value={fmt(k.new_assets)} />
        <StatCard label={t('Giorni con uso')} value={`${k.active_days}/${st.days}`} />
        <StatCard label={t('Fuori orario')} value={`${pct(quando.fuori, quando.totale)}%`} />
        <StatCard label={t('Volume')} value={k.bytes ? formatBytes(k.bytes) : '—'} />
      </div>

      <Card
        title={t('Andamento nel tempo')}
        actions={
          <div className="segmented" role="group" aria-label={t('Raggruppa per')}>
            <button type="button" className={vista === 'service' ? 'segmented__on' : ''} aria-pressed={vista === 'service'} onClick={() => setVista('service')}>{t('per servizio')}</button>
            <button type="button" className={vista === 'category' ? 'segmented__on' : ''} aria-pressed={vista === 'category'} onClick={() => setVista('category')}>{t('per tipo')}</button>
          </div>
        }
      >
        <p className="field__hint" style={{ marginTop: 0 }}>
          {t('Usi per giorno, dal {da} al {a}.', { da: formatDay(st.from), a: formatDay(st.to) })}
          {vista === 'service' && serieServizi.some((s) => s.key === '__altri__') && ` ${t('I primi quattro servizi hanno una tinta propria, gli altri sono sommati in «altri servizi».')}`}
        </p>
        <StackedCountSeries points={punti} series={vista === 'service' ? serieServizi : serieCategorie}
                            label={t('Usi per giorno')} altezza={220} />
      </Card>

      <div className="grid grid--2">
        <Card title={t('Per servizio')}>
          <CategoryBars data={st.services.map((s) => ({ key: s.service, label: s.service, value: s.count }))} />
        </Card>
        <Card title={t('Per tipo')}>
          <Donut centreLabel={t('usi')}
                 slices={serieCategorie.map((c) => ({
                   key: c.key, label: c.label, color: c.color,
                   value: st.categories.find((x) => x.category === c.key)?.count ?? 0,
                 }))} />
        </Card>
      </div>

      <Card title={t('Quando')}>
        <p className="field__hint" style={{ marginTop: 0 }}>
          {t('Usi per giorno della settimana e ora, nell’ora locale. Fuori orario (prima delle 8, dalle 19 o nel fine settimana): {f}% · nel fine settimana: {w}% · ora più intensa: {h}.', {
            f: pct(quando.fuori, quando.totale), w: pct(quando.weekend, quando.totale),
            h: `${String(quando.oraPiena).padStart(2, '0')}:00–${String((quando.oraPiena + 1) % 24).padStart(2, '0')}:00`,
          })}
        </p>
        <HeatmapSettimana celle={celle} giorni={giorniSettimana} unita={t('usi')} />
      </Card>

      <div className="grid grid--2">
        <Card title={t('Adozione')}>
          <p className="field__hint" style={{ marginTop: 0 }}>
            {t('Asset che usano un’AI per la prima volta, giorno per giorno. In tutto {n} asset l’hanno usata almeno una volta.', { n: fmt(ultimoCumulato) })}
          </p>
          <CountSeries points={st.adoption.map((a) => ({ date: a.day, value: a.new_assets }))}
                       label={t('Asset alla prima volta')} altezza={160}
                       emptyHint={t('Nessun asset nuovo nel periodo.')} />
        </Card>
        <Card title={t('Da dove arriva il dato')}>
          <CategoryBars data={st.sources.map((f) => ({ key: f.source, label: etichettaFonteStat(f.source), value: f.count }))}
                        emptyHint={t('Nessuna fonte nel periodo.')} />
          <p className="field__hint">
            {t('Un uso visto da due fonti conta per entrambe. Il {p}% degli asset più attivi ({n} su {m}) fa il {q}% degli usi.', {
              p: 10, n: st.concentration.top_assets, m: st.concentration.assets, q: Math.round(st.concentration.share * 100),
            })}
          </p>
          {st.new_services.length > 0 && (
            <p className="field__hint">
              <strong>{t('Servizi nuovi nel periodo')}:</strong>{' '}
              {st.new_services.map((n) => `${n.service} (${formatDay(n.first_day)})`).join(', ')}
            </p>
          )}
        </Card>
      </div>

      <Card title={t('Servizi nel dettaglio')}>
        <DataTable
          rows={st.services}
          columns={colonneServizi}
          rowKey={(s) => s.service}
          conteggio={{ nome: t('servizi') }}
        />
      </Card>
    </>
  )
}

/** The columns of «Who uses AI» (a function: `t()` at render time). */
const COLONNE_PER_ASSET = (): Column<AiUsageByAsset['rows'][number]>[] => [
  { key: 'pc', header: t('Asset') },
  { key: 'user', header: t('Utente') },
  { key: 'services', header: t('Servizi'), value: (r) => r.services.join(', ') },
  { key: 'sources', header: t('Fonte'), value: (r) => (r.sources ?? []).map(etichettaFonte).join(', ') },
  { key: 'count', header: t('Usi'), type: 'number' },
  {
    key: 'bytes',
    header: t('Volume'),
    type: 'number',
    value: (r) => r.bytes || null,
    render: (r) => (r.bytes ? formatBytes(r.bytes) : '—'),
  },
]

function PerAsset({ periodo }: { periodo: AiPeriodo }) {
  const [pcKey, setPcKey] = useState('')
  const [userKey, setUserKey] = useState('')
  const [dati, setDati] = useState<AiUsageByAsset | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  const [caricando, setCaricando] = useState(false)

  async function carica() {
    setCaricando(true)
    setErrore(null)
    try {
      setDati(await api.aiUsageByAsset({
        days: 'days' in periodo ? periodo.days : 30,
        from_date: 'from' in periodo ? periodo.from : undefined,
        to_date: 'to' in periodo ? periodo.to : undefined,
        pc_key: pcKey || undefined, user_key: userKey || undefined,
      }))
    } catch (e) {
      setErrore(e)
    } finally {
      setCaricando(false)
    }
  }

  return (
    <Card title={t('Chi usa le AI')}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Per asset. Se il rapporto è anonimizzato, PC e utente sono pseudonimi: inserisci la chiave di disoffuscamento per rivelare la macchina (livello 1) o anche l’utente (livello 2).')}
      </p>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
        <label className="field" style={{ marginBottom: 0 }}>
          <span>{t('Chiave PC (facoltativa)')}</span>
          <input type="password" autoComplete="off" value={pcKey} onChange={(e) => setPcKey(e.target.value)} />
        </label>
        <label className="field" style={{ marginBottom: 0 }}>
          <span>{t('Chiave utente (facoltativa)')}</span>
          <input type="password" autoComplete="off" value={userKey} onChange={(e) => setUserKey(e.target.value)} />
        </label>
        <button onClick={carica} disabled={caricando}>{caricando ? t('Carico…') : t('Mostra')}</button>
      </div>
      <ErrorBanner error={errore} />
      {dati && dati.anonymized && (
        <p className="banner banner--warn">
          {dati.level === 1
            ? t('PC in chiaro, utente anonimizzato: serve la chiave utente per il livello 2.')
            : t('Rapporto anonimizzato: PC e utente sono pseudonimi. Inserisci una chiave per rivelarli.')}
        </p>
      )}
      {dati && (
        !dati.rows.length ? (
          <EmptyState title={t('Nessun asset')} hint={t('Nessun uso di AI nel periodo scelto.')} />
        ) : (
          <DataTable
            rows={dati.rows}
            columns={COLONNE_PER_ASSET()}
            rowKey={(r, i) => `${r.pc}|${r.user}|${i}`}
            conteggio={{ nome: t('asset') }}
          />
        )
      )}
    </Card>
  )
}

function etichettaFonte(f: string): string {
  if (f === 'sni') return t('sonda (SNI)')
  if (f === 'agent') return t('agente')
  if (f === 'netflow') return t('flussi NetFlow')
  return f
}

function Privacy() {
  const admin = useAmministra()
  const dati = useAsync(() => api.aiUsagePrivacy(), [])
  const [pcKey, setPcKey] = useState('')
  const [userKey, setUserKey] = useState('')
  const [errore, setErrore] = useState<unknown>(null)
  const [salvato, setSalvato] = useState(false)

  if (dati.loading && !dati.data) return null
  const v = dati.data

  async function salva(patch: { anonymize?: boolean; pc_key?: string | null; user_key?: string | null }) {
    setErrore(null)
    setSalvato(false)
    try {
      await api.aiUsagePrivacySave(patch)
      setPcKey('')
      setUserKey('')
      setSalvato(true)
      dati.reload()
    } catch (e) {
      setErrore(e)
    }
  }

  return (
    <Card title={t('Privacy')}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('«Chi usa le AI» sfiora la sorveglianza dei lavoratori: puoi anonimizzare il rapporto e tenere due chiavi per rivelare chi serve. La chiave PC (livello 1) svela la macchina, la chiave utente (livello 2) anche l’utente. Le chiavi non si rileggono mai in chiaro.')}
      </p>
      <ErrorBanner error={errore} />
      {salvato && <p className="banner banner--ok">{t('Impostazioni salvate.')}</p>}

      <label className="check" style={{ display: 'block', marginBottom: 12 }}>
        <input type="checkbox" checked={v?.anonymize ?? false} disabled={!admin}
          onChange={(e) => salva({ anonymize: e.target.checked })} />{' '}
        {t('Anonimizza il rapporto (PC e utente come pseudonimi)')}
      </label>

      {admin && (
        <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label className="field" style={{ marginBottom: 0 }}>
            <span>{t('Chiave PC (livello 1)')}</span>
            <input type="password" autoComplete="new-password" value={pcKey}
              placeholder={v?.has_pc_key ? t('impostata — lascia vuoto per non cambiarla') : ''}
              onChange={(e) => setPcKey(e.target.value)} />
          </label>
          <label className="field" style={{ marginBottom: 0 }}>
            <span>{t('Chiave utente (livello 2)')}</span>
            <input type="password" autoComplete="new-password" value={userKey}
              placeholder={v?.has_user_key ? t('impostata — lascia vuoto per non cambiarla') : ''}
              onChange={(e) => setUserKey(e.target.value)} />
          </label>
          <button disabled={!pcKey && !userKey}
            onClick={() => salva({ pc_key: pcKey || undefined, user_key: userKey || undefined })}>
            {t('Salva le chiavi')}
          </button>
          {(v?.has_pc_key || v?.has_user_key) && (
            <button className="secondary" onClick={() => salva({ pc_key: '', user_key: '' })}>
              {t('Togli le chiavi')}
            </button>
          )}
        </div>
      )}
      {!admin && <p className="field__hint">{t('Solo un amministratore del tenant può cambiare le chiavi.')}</p>}
    </Card>
  )
}
